import type {
	IDexfSettingsKompisUrls,
	ILocalizedInformation,
	ILocalizedTranslations
} from '@inter-ikea-kompis/types';

import type IAddToBagTranslations from '../types/IAddToBagTranslations.js';

type IAddToBagToastMessage = {
	label: string;
	actionHref: string;
	actionLabel: string;
};

export default class AddToBagToastMessage {
	public static getAddToCartSuccess(
		localizedInformationOrTranslations: ILocalizedInformation
	): IAddToBagToastMessage;
	public static getAddToCartSuccess(
		localizedInformationOrTranslations: IAddToBagTranslations,
		urls: IDexfSettingsKompisUrls
	): IAddToBagToastMessage;
	public static getAddToCartSuccess(
		localizedInformationOrTranslations: ILocalizedInformation | IAddToBagTranslations,
		urls?: IDexfSettingsKompisUrls
	): IAddToBagToastMessage {
		const { addToShoppingCartSuccessHeadline, addToShoppingCartName } = this.getToastTranslations(
			localizedInformationOrTranslations
		);

		if ('urls' in localizedInformationOrTranslations) {
			return {
				label: addToShoppingCartSuccessHeadline,
				actionHref: localizedInformationOrTranslations.urls.shoppingCartLink,
				actionLabel: addToShoppingCartName
			};
		} else if (urls) {
			return {
				label: addToShoppingCartSuccessHeadline,
				actionHref: urls.shoppingCartLink,
				actionLabel: addToShoppingCartName
			};
		}

		throw new Error(
			"URLs must either be provided through the 'urls' parameter or by using the ILocalizedInformation type for the 'localizedInformationOrTranslations' parameter."
		);
	}

	public static getAddToCartFailure(
		localizedInformationOrTranslations: ILocalizedInformation
	): IAddToBagToastMessage;
	public static getAddToCartFailure(
		localizedInformationOrTranslations: IAddToBagTranslations,
		urls: IDexfSettingsKompisUrls
	): IAddToBagToastMessage;
	public static getAddToCartFailure(
		localizedInformationOrTranslations: ILocalizedInformation | IAddToBagTranslations,
		urls?: IDexfSettingsKompisUrls
	): IAddToBagToastMessage {
		const { addToShoppingCartFailureHeadline, addToShoppingCartFailureLink } =
			this.getToastTranslations(localizedInformationOrTranslations);

		if ('urls' in localizedInformationOrTranslations) {
			return {
				label: addToShoppingCartFailureHeadline,
				actionHref: localizedInformationOrTranslations.urls.shoppingCartLink,
				actionLabel: addToShoppingCartFailureLink
			};
		} else if (urls) {
			return {
				label: addToShoppingCartFailureHeadline,
				actionHref: urls.shoppingCartLink,
				actionLabel: addToShoppingCartFailureLink
			};
		}

		throw new Error(
			"URLs must either be provided through the 'urls' parameter or by using the ILocalizedInformation type for the 'localizedInformationOrTranslations' parameter."
		);
	}

	public static getAddToCartFail(
		localizedInformationOrTranslations: ILocalizedInformation
	): IAddToBagToastMessage;
	public static getAddToCartFail(
		localizedInformationOrTranslations: IAddToBagTranslations,
		urls: IDexfSettingsKompisUrls
	): IAddToBagToastMessage;
	public static getAddToCartFail(
		localizedInformationOrTranslations: ILocalizedInformation | IAddToBagTranslations,
		urls?: IDexfSettingsKompisUrls
	): IAddToBagToastMessage {
		const { addToShoppingBagAndListErrorMessage, addToShoppingListName } =
			this.getToastTranslations(localizedInformationOrTranslations);

		if ('urls' in localizedInformationOrTranslations) {
			return {
				label: addToShoppingBagAndListErrorMessage,
				actionHref: localizedInformationOrTranslations.urls.shoppingListLink,
				actionLabel: addToShoppingListName
			};
		} else if (urls) {
			return {
				label: addToShoppingBagAndListErrorMessage,
				actionHref: urls.shoppingListLink,
				actionLabel: addToShoppingListName
			};
		}

		throw new Error(
			"URLs must either be provided through the 'urls' parameter or by using the ILocalizedInformation type for the 'localizedInformationOrTranslations' parameter."
		);
	}

	private static getToastTranslations(
		localizedInformationOrTranslations: ILocalizedInformation | IAddToBagTranslations
	): ILocalizedTranslations | IAddToBagTranslations {
		if ('translations' in localizedInformationOrTranslations) {
			return localizedInformationOrTranslations.translations;
		}

		return localizedInformationOrTranslations;
	}
}
